<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useAuth } from './plugins/useAuth'
import { msalInstance, state } from './config/msalConfig'
import { useDefaultStore } from './stores/defaultStore'
import { NConfigProvider, darkTheme, lightTheme, type GlobalThemeOverrides } from 'naive-ui';
import { useRoute, useRouter } from 'vue-router';
import hljs from 'highlight.js/lib/core'
import json from 'highlight.js/lib/languages/json'
import { checkHealth } from './plugins/api';

hljs.registerLanguage('json', json)
hljs.registerLanguage('naive-log', () => ({
  contains: [
    {
      classNumber: 'number',
      begin: /\d+/
    }
  ]
}))

const themeOverrides: GlobalThemeOverrides = {
  common: {
    fontWeightStrong: '600'
  }
}

const route = useRoute()
const router = useRouter()
const defaultStore = useDefaultStore()
const authenticating = ref(false)
const connecting = ref(true)
const { handleRedirect, login } = useAuth()

watch(() => router.currentRoute.value.name, (to, from) => {
  if (from === 'error') {
    tryAuthenticate()
  }
})

async function tryAuthenticate() {
  try {
    authenticating.value = true

    await msalInstance.initialize()
    await handleRedirect() // This will return regardless of outcome

    if (state.isAuthenticated) {
      authenticating.value = false

      if (route.name === 'error') {
        router.back()
      }
    } else {
      login()
    }
  } catch (error) {
    console.error('Startup error:', error)
    router.push('/error?status=500&title=Error during app startup&description=It was not possible to start the app')
  } finally {
    defaultStore.loading = false
  }
}

onMounted(async () => {
  console.debug('App.vue mounted')

  defaultStore.loading = true

  try {
    await checkHealth()
  } catch (error) {
    console.error('Startup error:', error)
    router.push('/error?status=500&title=Server not responding&description=There seems to be a problem connecting to the server')
    return
  } finally {
    connecting.value = false
  }

  tryAuthenticate()
})
</script>

<template>
  <n-config-provider
    :theme="lightTheme"
    :theme-overrides="themeOverrides"
    :hljs="hljs"
  >
    <n-loading-bar-provider
      :loading-bar-style="{ loading: 'height: 6px;', error: 'height: 6px;' }"
    >
      <n-notification-provider>
        <n-message-provider>
          <div>
            <div v-if="route.meta.anonymous || state.isAuthenticated || route.name === 'error'">
              <RouterView />
            </div>

            <div v-else-if="connecting || authenticating" class="w-screen h-screen flex flex-col justify-center items-center gap-2">
              <n-spin></n-spin>

              <span v-if="connecting">Connecting</span>
              <span v-else-if="authenticating">Authenticating</span>
              <span v-else>Working</span>
            </div>
            <div v-else>
              Some unknown error happened!
            </div>
          </div>
        </n-message-provider>
      </n-notification-provider>
    </n-loading-bar-provider>
  </n-config-provider>
</template>